import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/Footer.css';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const [copiedEmail, setCopiedEmail] = useState('');

  const copyToClipboard = (email) => {
    navigator.clipboard.writeText(email).then(() => {
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(''), 2000);
    });
  };

  const handleSectionNavigation = (sectionId) => {
    // Si ya estamos en la página principal, solo hacer scroll
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Si estamos en otra página, navegar a la principal con el hash
      navigate(`/#${sectionId}`);
    }
  };

  const footerSections = [
    {
      title: "Producto",
      links: [
        { name: "Características", href: "#tools" },
        { name: "Integraciones", href: "#integrations" },
        { name: "Canales", href: "#channels" },
        { name: "Precios", href: "#plans" },
        { name: "Demo", href: "#demo" },
        { name: "Registro de cambios", href: "/log", isRoute: true }
      ]
    },
    {
      title: "Recursos",
      links: [
        { name: "Blog", href: "/blog", isRoute: true },
        { name: "Documentación", href: "/docs", isRoute: true }
      ]
    },
    {
      title: "Empresa",
      links: [
        { name: "Sobre nosotros", href: "/sobre-nosotros", isRoute: true },
        { name: "Carreras", href: "/carreras", isRoute: true }
      ]
    }
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      icon: "fab fa-linkedin-in",
      href: "https://www.linkedin.com/company/103662838",
      color: "#0077b5"
    },
    {
      name: "Instagram",
      icon: "fab fa-instagram",
      href: "https://www.instagram.com/runamatic/",
      color: "#e4405f"
    },
    {
      name: "Facebook",
      icon: "fab fa-facebook-f",
      href: "https://www.facebook.com/profile.php?id=61559663574965",
      color: "#4267b2"
    },
    {
      name: "YouTube",
      icon: "fab fa-youtube",
      href: "https://www.youtube.com/@Runamatic",
      color: "#ff0000"
    }
  ];

  const contactInfo = [
    {
      title: "¿Necesitas soporte o tienes preguntas?",
      icon: "fas fa-envelope",
      text: "hola@runamatic.io",
      email: "hola@runamatic.io"
    }
  ];

  return (
    <footer className="footer">
      <div className="footer-background">
        <div className="footer-shapes">
          <div className="footer-shape footer-shape-1"></div>
          <div className="footer-shape footer-shape-2"></div>
          <div className="footer-shape footer-shape-3"></div>
        </div>
      </div>

      <div className="footer-container">
        {/* Main Footer Content */}
        <div className="footer-main">
          {/* Left Column - Company Info */}
          <motion.div 
            className="footer-left"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="footer-logo">
              <span className="logo-text">Runamatic</span>
              <div className="logo-tagline">Tecnología para empresas</div>
            </div>
            <p className="footer-description">
              La plataforma líder en automatización de atención al cliente que ayuda a las empresas 
              a crecer más rápido con conversaciones inteligentes y procesos optimizados.
            </p>

            {/* Social Links */}
            <div className="footer-social">
              <h4>Síguenos</h4>
              <div className="social-links">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    className="social-link"
                    style={{ '--social-color': social.color }}
                    whileHover={{ y: -3, scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                    aria-label={social.name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className={social.icon}></i>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Center Column - Navigation */}
          <motion.div 
            className="footer-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="footer-links">
              {footerSections.map((section, index) => (
                <div key={index} className="footer-section">
                  <h4>{section.title}</h4>
                  <ul>
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        {link.isRoute ? (
                          <Link to={link.href}>
                            <motion.div
                              whileHover={{ x: 5 }}
                              transition={{ duration: 0.2 }}
                            >
                              {link.name}
                            </motion.div>
                          </Link>
                        ) : (
                          <motion.button
                            type="button"
                            className="footer-link-button"
                            whileHover={{ x: 5 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => {
                              const sectionId = link.href.substring(1); // Remover el #
                              handleSectionNavigation(sectionId);
                            }}
                          >
                            {link.name}
                          </motion.button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Contact */}
          <motion.div 
            className="footer-right"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 className="contact-section-title">Contacto</h4>
            <div className="footer-contact">
              {contactInfo.map((contact, index) => (
                <div key={index} className="contact-group">
                  <p className="contact-title">{contact.title}</p>
                  <motion.button
                    className={`contact-item ${copiedEmail === contact.email ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(contact.email)}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                  >
                    <i className={contact.icon}></i>
                    <span>{contact.text}</span>
                    {copiedEmail === contact.email && (
                      <span className="copied-indicator">
                        <i className="fas fa-check"></i>
                      </span>
                    )}
                  </motion.button>
                </div>
              ))}

              <div className="contact-group">
                <p className="contact-title">Contáctanos a nuestro Telegram</p>
                <motion.a
                  href="https://t.me/Runamatic_auto_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-item contact-item--telegram"
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  <i className="fab fa-telegram-plane"></i>
                  <span>Abrir Telegram</span>
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer Bottom */}
        <motion.div 
          className="footer-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="footer-bottom-content">
            <div className="footer-copyright">
              <p>&copy; {currentYear} Runamatic. Todos los derechos reservados.</p>
            </div>
            
            <div className="footer-certifications">
              <div className="certification-badge">
                <i className="fas fa-shield-alt"></i>
                <span>ISO 27001</span>
              </div>
              <div className="certification-badge">
                <i className="fas fa-lock"></i>
                <span>SOC 2</span>
              </div>
              <div className="certification-badge">
                <i className="fab fa-meta"></i>
                <span>Meta Partner</span>
              </div>
            </div>

            <div className="footer-bottom-links">
              <Link to="/tyc">Términos</Link>
              <Link to="/privacy-policy">Privacidad</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer; 